/**
 * Fase 20 (P0) — Baixa automática de estoque de bebidas.
 *
 * O CRUD de /estoque gerencia `db.beverages` com {name, price, stock, category}.
 * Até a Fase 7, só o fluxo legado de MESAS (TableDetailPage) baixava estoque.
 * As comandas modernas (GarcomPage → addItemToCommand) e o delivery NÃO
 * estavam decrementando — saldo exibido ficava irreal.
 *
 * Esta lib centraliza a baixa:
 *   - decrementBeverageStock(nameOrId, qty)  → baixa por nome OU id
 *   - incrementBeverageStock(...)            → reverte (uso futuro p/ cancelamento)
 *
 * Resiliente a nomes divergentes (ex: bebida no cardápio de delivery pode
 * ter nome ligeiramente diferente da bebida no estoque). Matching exato
 * preferencial; fallback case-insensitive.
 */
import db from '@/db';

async function findBeverage({ beverageId, name }) {
  if (beverageId) {
    const byId = await db.beverages.get(Number(beverageId));
    if (byId) return byId;
  }
  if (!name) return null;
  const all = await db.beverages.toArray();
  const exact = all.find((b) => b.name === name);
  if (exact) return exact;
  const normalized = name.trim().toLowerCase();
  return all.find((b) => (b.name || '').trim().toLowerCase() === normalized) || null;
}

/**
 * Baixa estoque. Retorna { ok, previous, next, bevId } ou { ok:false, reason }.
 * Silencioso — nunca lança; chamador pode logar se quiser.
 */
export async function decrementBeverageStock({ beverageId, name, qty = 1 }) {
  try {
    const bev = await findBeverage({ beverageId, name });
    if (!bev) return { ok: false, reason: 'not_found', name };
    const previous = Number(bev.stock || 0);
    const next = Math.max(0, previous - Number(qty || 1));
    await db.beverages.update(bev.id, { stock: next });
    return { ok: true, bevId: bev.id, previous, next };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[STOCK] decrement falhou (ignorado):', err?.message || err);
    return { ok: false, reason: 'error', error: String(err?.message || err) };
  }
}

export async function incrementBeverageStock({ beverageId, name, qty = 1 }) {
  try {
    const bev = await findBeverage({ beverageId, name });
    if (!bev) return { ok: false, reason: 'not_found', name };
    const previous = Number(bev.stock || 0);
    const next = previous + Number(qty || 1);
    await db.beverages.update(bev.id, { stock: next });
    return { ok: true, bevId: bev.id, previous, next };
  } catch (err) {
    return { ok: false, reason: 'error', error: String(err?.message || err) };
  }
}

/**
 * Baixa em lote para carrinho do /delivery (array de {name, quantity}).
 * Filtra automaticamente apenas itens cujo name bate em algum beverage
 * cadastrado — quentinha/marmita ignorada.
 */
export async function decrementCartBeverages(cartItems) {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return [];
  const results = [];
  for (const item of cartItems) {
    const r = await decrementBeverageStock({ name: item.name, qty: item.quantity || 1 });
    if (r.ok) results.push({ name: item.name, ...r });
  }
  return results;
}
